// Assessor Application Detail View - Shows comprehensive lab details with accept/reject functionality
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Button,
    Grid,
    Card,
    CardContent,
    Breadcrumbs,
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Tabs,
    Tab,
    TabPanel
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Assignment as AssignmentIcon,
    ExpandMore as ExpandMoreIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    LocationOn as LocationOnIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    Science as ScienceIcon,
    Build as BuildIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import AssessorApiService from '../../../services/AssessorApiService';
import { decryptAppId, decryptText } from '../../../utils/encryption';

// Custom TabPanel component
function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const ApplicationDetailView = () => {
    // State management
    const [applicationData, setApplicationData] = useState(null);
    const [labDetails, setLabDetails] = useState(null);
    const [disciplines, setDisciplines] = useState([]);
    const [scopes, setScopes] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [assessmentSlots, setAssessmentSlots] = useState([]);
    const [cabDetails, setCABDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectRemarks, setRejectRemarks] = useState('');
    const [tabValue, setTabValue] = useState(0);
    
    // Router hooks
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Get encrypted parameters from URL
    const encryptedAppId = searchParams.get('appId');
    const encryptedOrgId = searchParams.get('orgId');
    const encryptedAssessorId = searchParams.get('assessorId');
    const encryptedStage = searchParams.get('stage');
    
    // Decrypt parameters
    const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
    const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
    const assessorId = encryptedAssessorId ? decryptText(encryptedAssessorId) : null;
    const stage = encryptedStage ? decryptText(encryptedStage) : null;

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');

    // Load application and lab details
    const loadApplicationDetails = async () => {
        if (!appId || !orgId) {
            setError('Invalid application parameters');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Load application basic details
            const appResponse = await AssessorApiService.getApplicationDetails({
                appId,
                orgId
            });

            if (appResponse.success && appResponse.data) {
                setApplicationData(appResponse.data);
                
                // Load detailed lab information
                const labResponse = await AssessorApiService.getLabDetails({
                    orgId,
                    appId
                });

                if (labResponse.success && labResponse.data) {
                    setLabDetails(labResponse.data);
                }

                // Load disciplines
                const disciplinesResponse = await AssessorApiService.getDisciplines({
                    orgId,
                    appId
                });

                if (disciplinesResponse.success && disciplinesResponse.data) {
                    setDisciplines(disciplinesResponse.data);
                }

                // Load scopes
                const scopesResponse = await AssessorApiService.getScopes({
                    orgId,
                    appId
                });

                if (scopesResponse.success && scopesResponse.data) {
                    setScopes(scopesResponse.data);
                }

                // Load equipment
                const equipmentResponse = await AssessorApiService.getEquipment({
                    orgId,
                    appId
                });

                if (equipmentResponse.success && equipmentResponse.data) {
                    setEquipment(equipmentResponse.data);
                }

                // Load assessment slots
                const slotsResponse = await AssessorApiService.getAssessmentSlots({
                    orgId,
                    appId
                });

                if (slotsResponse.success && slotsResponse.data) {
                    setAssessmentSlots(slotsResponse.data);
                }

                // Load CAB details
                const cabResponse = await AssessorApiService.getCABDetails({
                    appId
                });

                if (cabResponse.success && cabResponse.data) {
                    setCABDetails(cabResponse.data);
                }

            } else {
                setError('Failed to load application details');
            }

        } catch (err) {
            console.error('Load application details error:', err);
            setError(`Error loading application details: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle Accept Application
    const handleAccept = () => {
        setAcceptDialogOpen(true);
    };

    // Confirm Accept Application
    const confirmAccept = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await AssessorApiService.acceptApplication({
                appId,
                orgId,
                assessorId: userData.orgId
            });
            
            setSuccess('Application accepted successfully!');
            setAcceptDialogOpen(false);
            
            // Navigate back to applications list after a delay
            setTimeout(() => {
                navigate('/main/assessor/applications');
            }, 2000);
            
        } catch (err) {
            console.error('Accept application error:', err);
            setError(`Failed to accept application: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle Reject Application
    const handleReject = () => {
        setRejectRemarks('');
        setRejectDialogOpen(true);
    };

    // Confirm Reject Application
    const confirmReject = async () => {
        if (!rejectRemarks || rejectRemarks.trim() === '') {
            setError('Please provide remarks for rejection');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            await AssessorApiService.rejectApplication({
                appId,
                orgId,
                assessorId: userData.orgId,
                remarks: rejectRemarks
            });
            
            setSuccess('Application rejected successfully!');
            setRejectDialogOpen(false);
            setRejectRemarks('');
            
            // Navigate back to applications list after a delay
            setTimeout(() => {
                navigate('/main/assessor/applications');
            }, 2000);
            
        } catch (err) {
            console.error('Reject application error:', err);
            setError(`Failed to reject application: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Load data on component mount
    useEffect(() => {
        loadApplicationDetails();
    }, [appId, orgId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !applicationData) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/main/assessor/applications')}
                >
                    Back to Applications
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs sx={{ mb: 3 }}>
                <Link
                    underline="hover"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/main/assessor/applications')}
                >
                    Applications
                </Link>
                <Typography color="text.primary">Application Details</Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Application Details
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/main/assessor/applications')}
                >
                    Back to Applications
                </Button>
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Success Display */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {applicationData && (
                <>
                    {/* Basic Application Info Card */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                        <BusinessIcon sx={{ mr: 1 }} />
                                        Laboratory Information
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="body2">
                                            <strong>Name:</strong> {applicationData.LaboratoryName || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Application No:</strong> {applicationData.ApplicationNo || applicationData.TempAppNo || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Assessment Type:</strong> 
                                            <Chip 
                                                label={applicationData.AssessmentType || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                sx={{ ml: 1 }} 
                                            />
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Lab Type:</strong> 
                                            <Chip 
                                                label={applicationData.LabType || 'N/A'} 
                                                size="small" 
                                                color="secondary" 
                                                sx={{ ml: 1 }} 
                                            />
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Standard:</strong> {applicationData.Standard || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                        <LocationOnIcon sx={{ mr: 1 }} />
                                        Contact Information
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="body2">
                                            <strong>Address:</strong> {applicationData.Address || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>City:</strong> {applicationData.City || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>State:</strong> {applicationData.State || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Contact Person:</strong> {applicationData.ContactPerson || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Email:</strong> {applicationData.ContactEmail || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Mobile:</strong> {applicationData.ContactMobile || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Accept/Reject Actions */}
                    {stage === '19c' && (
                        <Card sx={{ mb: 3, border: '2px solid #e0e0e0' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                    <AssessmentIcon sx={{ mr: 1 }} />
                                    Assessment Decision
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                                    Please review the laboratory details below and make your decision to accept or reject this application.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        startIcon={<CheckIcon />}
                                        onClick={handleAccept}
                                        disabled={loading}
                                        sx={{ minWidth: 150 }}
                                    >
                                        Accept Application
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="large"
                                        startIcon={<CloseIcon />}
                                        onClick={handleReject}
                                        disabled={loading}
                                        sx={{ minWidth: 150 }}
                                    >
                                        Reject Application
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Detailed Information Tabs */}
                    <Card>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="application details tabs">
                                <Tab label="Lab Details" icon={<BusinessIcon />} />
                                <Tab label="Disciplines" icon={<ScienceIcon />} />
                                <Tab label="Scopes" icon={<AssignmentIcon />} />
                                <Tab label="Equipment" icon={<BuildIcon />} />
                                <Tab label="Assessment Plan" icon={<ScheduleIcon />} />
                            </Tabs>
                        </Box>

                        {/* Lab Details Tab */}
                        <CustomTabPanel value={tabValue} index={0}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Laboratory Details</Typography>
                            {labDetails ? (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                                    <BusinessIcon sx={{ mr: 1 }} />
                                                    Basic Information
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Organization Name:</strong> {labDetails.OrgName || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Lab Name:</strong> {labDetails.LabName || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Lab Type:</strong> {labDetails.LabType || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Standard:</strong> {labDetails.Standard || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Assessment Type:</strong> {labDetails.AssessmentType || 'N/A'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                                                    <LocationOnIcon sx={{ mr: 1 }} />
                                                    Address & Contact
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Address:</strong> {labDetails.Address || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>City:</strong> {labDetails.City || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>State:</strong> {labDetails.State || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>PIN Code:</strong> {labDetails.PinCode || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Phone:</strong> {labDetails.Phone || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    <strong>Email:</strong> {labDetails.Email || 'N/A'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Alert severity="info">No lab details available.</Alert>
                            )}
                        </CustomTabPanel>

                        {/* Disciplines Tab */}
                        <CustomTabPanel value={tabValue} index={1}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Disciplines</Typography>
                            {disciplines.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Sr. No.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Discipline Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Group</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {disciplines.map((discipline, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{discipline.DisciplineName || discipline.Name || 'N/A'}</TableCell>
                                                    <TableCell>{discipline.GroupName || discipline.Group || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={discipline.Status || 'Active'} 
                                                            size="small" 
                                                            color="success"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info">No disciplines found for this application.</Alert>
                            )}
                        </CustomTabPanel>

                        {/* Scopes Tab */}
                        <CustomTabPanel value={tabValue} index={2}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Scopes</Typography>
                            {scopes.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Sr. No.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Product/Material</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Test/Parameter</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Method</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Range</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {scopes.map((scope, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{scope.Product || scope.Material || 'N/A'}</TableCell>
                                                    <TableCell>{scope.TestParameter || scope.Parameter || 'N/A'}</TableCell>
                                                    <TableCell>{scope.Method || 'N/A'}</TableCell>
                                                    <TableCell>{scope.Range || 'N/A'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info">No scopes found for this application.</Alert>
                            )}
                        </CustomTabPanel>

                        {/* Equipment Tab */}
                        <CustomTabPanel value={tabValue} index={3}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Equipment</Typography>
                            {equipment.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Sr. No.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Equipment Name</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Make/Model</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Serial Number</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Calibration Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {equipment.map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{item.EquipmentName || item.Name || 'N/A'}</TableCell>
                                                    <TableCell>{`${item.Make || 'N/A'} / ${item.Model || 'N/A'}`}</TableCell>
                                                    <TableCell>{item.SerialNumber || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={item.CalibrationStatus || 'Valid'} 
                                                            size="small" 
                                                            color={item.CalibrationStatus === 'Expired' ? 'error' : 'success'}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info">No equipment found for this application.</Alert>
                            )}
                        </CustomTabPanel>

                        {/* Assessment Plan Tab */}
                        <CustomTabPanel value={tabValue} index={4}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Assessment Plan</Typography>
                            {assessmentSlots.length > 0 ? (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Sr. No.</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Assessment Date From</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Assessment Date To</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Remarks</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {assessmentSlots.map((slot, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{AssessorApiService.formatDate(slot.AssessmentDateFrom) || 'N/A'}</TableCell>
                                                    <TableCell>{AssessorApiService.formatDate(slot.AssessmentDateTo) || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={slot.Status || 'Pending'} 
                                                            size="small" 
                                                            color="primary"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{slot.Remarks || 'N/A'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Alert severity="info">No assessment slots found for this application.</Alert>
                            )}
                        </CustomTabPanel>
                    </Card>
                </>
            )}

            {/* Accept Confirmation Dialog */}
            <Dialog
                open={acceptDialogOpen}
                onClose={() => !loading && setAcceptDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Declaration and Acceptance</DialogTitle>
                <DialogContent>
                    {applicationData && (
                        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Laboratory: {applicationData.LaboratoryName}
                            </Typography>
                            <Typography variant="body2">
                                Application No: {applicationData.ApplicationNo || applicationData.TempAppNo}
                            </Typography>
                        </Box>
                    )}
                    
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                        I hereby declare the following:
                    </Typography>
                    
                    <Box sx={{ pl: 2, mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ fontWeight: 'bold', mr: 1, minWidth: '20px' }}>1.</Box>
                            <Box>I have not offered any consultancy, guidance, supervision or other services to the CAB (e.g. internal audit), in any way.</Box>
                        </Typography>
                        
                        <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ fontWeight: 'bold', mr: 1, minWidth: '20px' }}>2.</Box>
                            <Box>I got an opportunity to go through various documents like Quality Manual, Procedural Manuals, Work instructions, Internal reports etc. of the above CAB and other related information that might have been given by NABL. I undertake to maintain strict confidentiality of the information acquired in course of discharge of my responsibility and shall not disclose to any person other than that required by NABL.</Box>
                        </Typography>
                        
                        <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ fontWeight: 'bold', mr: 1, minWidth: '20px' }}>3.</Box>
                            <Box>I am not an Ex Employee of this CAB.</Box>
                        </Typography>
                        
                        <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                            <Box component="span" sx={{ fontWeight: 'bold', mr: 1, minWidth: '20px' }}>4.</Box>
                            <Box>I am not in relation with any person of the management of this CAB.</Box>
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setAcceptDialogOpen(false)}
                        disabled={loading}
                        variant="outlined"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmAccept}
                        variant="contained"
                        color="success"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                        size="large"
                    >
                        {loading ? 'Accepting...' : 'Accept All and Proceed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Confirmation Dialog */}
            <Dialog
                open={rejectDialogOpen}
                onClose={() => !loading && setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject Application</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Please provide remarks for rejecting this application:
                    </Typography>
                    {applicationData && (
                        <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Laboratory: {applicationData.LaboratoryName}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Remarks *"
                        placeholder="Enter reason for rejection..."
                        value={rejectRemarks}
                        onChange={(e) => setRejectRemarks(e.target.value)}
                        disabled={loading}
                        error={!rejectRemarks && error}
                        helperText={!rejectRemarks && error ? 'Remarks are required' : ''}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setRejectDialogOpen(false);
                            setRejectRemarks('');
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmReject}
                        variant="contained"
                        color="error"
                        disabled={loading || !rejectRemarks.trim()}
                        startIcon={loading ? <CircularProgress size={20} /> : <CloseIcon />}
                    >
                        {loading ? 'Rejecting...' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApplicationDetailView;